import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Lock, LogOut, CreditCard, Check } from "lucide-react";
import api from "@/lib/api";
import customerApi, { CUSTOMER_TOKEN, startGoogleLogin, downloadBlob, errMsg } from "@/lib/customerApi";
import { Section } from "@/components/common";

const STEPS = ["Received", "In Progress", "Ready", "Paid in Full", "Delivered"];
const inputCls = "w-full bg-[#0B0F17] border border-white/10 rounded-lg px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-blue-500";

function LoginCard({ onAuthed }) {
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = mode === "login" ? await api.post("/auth/login", { email: f.email, password: f.password })
        : await api.post("/customer/register", f);
      localStorage.setItem(CUSTOMER_TOKEN, data.token);
      onAuthed();
    } catch (err) { toast.error(errMsg(err, "Sign-in failed")); }
  };
  return (
    <Section className="py-20 max-w-md">
      <form onSubmit={submit} className="card p-8 space-y-4" data-testid="account-login-form">
        <h1 className="font-display font-bold text-2xl text-white">{mode === "login" ? "Customer sign in" : "Create your account"}</h1>
        <p className="text-slate-400 text-sm">Track your Power Feasibility Study and download your reports.</p>
        {mode === "register" && <input data-testid="account-name" required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />}
        <input data-testid="account-email" type="email" required placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
        <input data-testid="account-password" type="password" required placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} />
        <button data-testid="account-submit" className="w-full bg-[#2563EB] hover:bg-[#3B82F6] text-white font-semibold py-2.5 rounded-lg">{mode === "login" ? "Sign in" : "Create account"}</button>
        <button type="button" data-testid="account-google-btn" onClick={() => startGoogleLogin("/account")} className="w-full border border-white/15 hover:border-blue-500/60 text-white font-semibold py-2.5 rounded-lg">Continue with Google</button>
        <button type="button" data-testid="account-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="w-full text-sm text-[#3B82F6]">
          {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
        </button>
      </form>
    </Section>
  );
}

const Stepper = ({ status }) => {
  const idx = STEPS.indexOf(status);
  return (
    <ol className="grid grid-cols-5 gap-1.5 mt-5" data-testid="order-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className="text-center">
          <div className={`h-1.5 rounded-full ${i <= idx ? "bg-[#84CC16]" : "bg-white/10"}`} />
          <p className={`text-[10px] sm:text-xs mt-1.5 ${i <= idx ? "text-white" : "text-slate-500"}`}>{s}</p>
        </li>
      ))}
    </ol>
  );
};

function OrderCard({ o }) {
  const dl = (r) => downloadBlob(customerApi, `/power-study/files/${r.id}`, r.filename).catch((e) => toast.error(errMsg(e, "Download failed")));
  return (
    <div className="card p-6" data-testid={`customer-order-${o.order_number}`}>
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <p className="font-mono text-xs text-[#84CC16]">{o.order_number}</p>
          <p className="text-white font-semibold mt-1">{o.site_address || `Parcel ${o.parcel_id}`}</p>
          <p className="text-slate-400 text-sm">{o.acreage} acres · {o.target_mw} MW target{o.utilities ? ` · ${o.utilities}` : ""}</p>
        </div>
        <span data-testid="customer-order-status" className="h-fit text-xs font-semibold px-3 py-1 rounded-full bg-[#2563EB]/20 text-blue-200 border border-blue-500/30">{o.status}</span>
      </div>
      <Stepper status={o.status} />
      {o.status === "Ready" && o.balance_invoice_url && (
        <a href={o.balance_invoice_url} target="_blank" rel="noopener noreferrer" data-testid="pay-balance-btn"
          className="mt-5 inline-flex items-center gap-2 bg-[#84CC16] text-[#0B0F17] font-bold px-5 py-2.5 rounded-full"><CreditCard size={16} /> Pay $3,500 balance</a>
      )}
      <div className="mt-5 space-y-2">
        {o.reports.length === 0 && <p className="text-slate-500 text-sm">Your report files will appear here when they're ready.</p>}
        {o.reports.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 bg-[#0B0F17] border border-white/10 rounded-lg px-4 py-3">
            <span className="text-sm text-slate-200">{r.label}</span>
            {r.locked ? <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Lock size={13} /> Unlocks after balance is paid</span>
              : <button data-testid={`download-${r.id}`} onClick={() => dl(r)} className="inline-flex items-center gap-1.5 text-sm text-[#3B82F6] hover:text-[#84CC16]"><Download size={15} /> Download</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Account() {
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState(null);
  const [checking, setChecking] = useState(true);

  const load = useCallback(async () => {
    if (!localStorage.getItem(CUSTOMER_TOKEN)) { setChecking(false); return; }
    try {
      const [u, o] = await Promise.all([customerApi.get("/auth/me"), customerApi.get("/customer/orders")]);
      setMe(u.data); setOrders(o.data);
    } catch { localStorage.removeItem(CUSTOMER_TOKEN); setMe(null); }
    setChecking(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (checking) return <p className="text-slate-400 font-mono text-center py-24">Loading…</p>;
  if (!me) return <LoginCard onAuthed={load} />;
  const logout = () => { localStorage.removeItem(CUSTOMER_TOKEN); setMe(null); setOrders(null); };

  return (
    <Section className="py-14 max-w-4xl" >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8" data-testid="customer-dashboard">
        <div>
          <p className="eyebrow mb-1">My account</p>
          <h1 className="font-display font-bold text-2xl sm:text-3xl text-white">Power Feasibility Studies</h1>
          <p className="text-slate-400 text-sm mt-1">{me.email}</p>
        </div>
        <button data-testid="customer-logout" onClick={logout} className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm"><LogOut size={15} /> Sign out</button>
      </div>
      <div className="space-y-5">
        {orders?.length === 0 && (
          <div className="card p-8 text-center" data-testid="customer-no-orders">
            <Check className="mx-auto text-slate-500 mb-2" />
            <p className="text-slate-300">No paid orders yet.</p>
            <Link to="/power-study" className="text-[#3B82F6] underline text-sm">Order a DYOR Power Feasibility Study</Link>
          </div>
        )}
        {orders?.map((o) => <OrderCard key={o.id} o={o} />)}
      </div>
    </Section>
  );
}
